<template>
  <div class="cart_page">
    <div class="cart_head">
      <h1>장바구니</h1>
      <p v-if="isLoaded && !isEmpty">
        콘텐츠 <b>{{ contentCartInfo.contents?.length || 0 }}</b> · 상품
        <b>{{ allProducts.length }}</b>
      </p>
    </div>

    <!-- 빈 장바구니 -->
    <div v-if="isEmpty" class="cart_empty">
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path
          d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"
        />
      </svg>
      <p>장바구니가 비어 있어요.</p>
      <router-link to="/mall" class="cart_ghost_btn">콘텐츠 둘러보기</router-link>
    </div>

    <!-- 장바구니 + 주문서: 데스크톱은 [목록·주문자 정보 | 결제 영역] 2단 -->
    <div v-else-if="isLoaded" class="cart_layout">
      <div class="cart_main">
        <section class="cart_card cart_list" aria-label="장바구니 목록">
          <!-- 전체선택 바 (스크롤해도 상단에 고정) -->
          <div class="cart_list_bar">
            <label class="cart_check">
              <input
                type="checkbox"
                :checked="isAllSelected"
                :disabled="selectableItems.length === 0"
                @change="toggleItems(selectableItems, $event)"
              />
              <span>
                전체선택 <b>{{ selectedCount }}</b> /
                {{ selectableItems.length }}
              </span>
            </label>
            <button type="button" class="cart_ghost_btn" @click="deleteSelected">
              선택삭제
            </button>
          </div>

          <!-- 콘텐츠 -->
          <div v-if="contentAvailableCartList.length > 0" class="cart_group">
            <div class="cart_group_head">
              <label class="cart_check">
                <input
                  type="checkbox"
                  :checked="contentAvailableCartList.every((item) => item.isSelected)"
                  @change="toggleItems(contentAvailableCartList, $event)"
                />
                <span>콘텐츠</span>
              </label>
              <span class="cart_group_meta">결제 후 바로 내려받기</span>
            </div>

            <ul>
              <li
                v-for="item in contentAvailableCartList"
                :key="item.cartId"
                class="cart_item"
              >
                <label class="cart_check cart_item_check">
                  <input
                    v-model="item.isSelected"
                    type="checkbox"
                    :aria-label="item.title + ' 선택'"
                  />
                </label>
                <router-link
                  :to="'/mall/view/' + item.itemId"
                  class="cart_item_thumb"
                >
                  <img
                    v-if="item.thumbnailLink"
                    :src="IMAGE_BASE + item.thumbnailLink"
                    :alt="item.title"
                  />
                  <img v-else src="@/assets/img/null_img.jpg" :alt="item.title" />
                </router-link>
                <div class="cart_item_info">
                  <span v-if="item.itemKind" class="cart_item_badge">{{
                    item.itemKind
                  }}</span>
                  <router-link
                    :to="'/mall/view/' + item.itemId"
                    class="cart_item_title"
                    >{{ item.title }}</router-link
                  >
                </div>
                <div class="cart_item_price">
                  <p v-if="hasItemDiscount(item)">
                    <strong>{{ itemDiscountRate(item) }}%</strong>
                    <s>{{ won(item.regularPrice) }}</s>
                  </p>
                  <b>{{ item.isFree === true ? "무료" : won(item.purchasePrice) }}</b>
                </div>
                <button
                  type="button"
                  class="cart_item_remove"
                  :aria-label="item.title + ' 삭제'"
                  @click="removeCartItems([item.cartId])"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </li>
            </ul>
          </div>

          <!-- 상품 (출고지별) -->
          <div
            v-for="(warehouse, wareKey) in productCartInfo.warehouses"
            :key="warehouse.warehouseId"
            class="cart_group"
          >
            <div class="cart_group_head">
              <label class="cart_check">
                <input
                  type="checkbox"
                  :checked="warehouse.products.every((product) => product.isSelected)"
                  @change="toggleItems(warehouse.products, $event)"
                />
                <span>{{ warehouse.name ?? "출고지" }}</span>
              </label>
              <span
                v-if="warehouse.deliveryFreeOption > 0"
                class="cart_group_meta"
              >
                {{ won(warehouse.deliveryFreeOption) }} 이상 무료배송
              </span>
            </div>

            <ul>
              <li
                v-for="product in warehouse.products"
                :key="product.cartId"
                class="cart_item"
              >
                <label class="cart_check cart_item_check">
                  <input
                    v-model="product.isSelected"
                    type="checkbox"
                    :aria-label="product.title + ' 선택'"
                  />
                </label>
                <router-link
                  :to="'/mall/view/' + product.itemId"
                  class="cart_item_thumb"
                >
                  <img
                    v-if="product.thumbnailLink"
                    :src="IMAGE_BASE + product.thumbnailLink"
                    :alt="product.title"
                  />
                  <img
                    v-else
                    src="@/assets/img/null_img.jpg"
                    :alt="product.title"
                  />
                </router-link>
                <div class="cart_item_info">
                  <router-link
                    :to="'/mall/view/' + product.itemId"
                    class="cart_item_title"
                    >{{ product.title }}</router-link
                  >
                  <ul
                    v-if="product.cartOptionList.length > 0"
                    class="cart_item_options"
                  >
                    <li
                      v-for="(option, optionIndex) in product.cartOptionList"
                      :key="optionIndex"
                    >
                      {{ option.optionPathNames.join(" > ") }}
                      <template v-if="option.optionMessage">
                        - {{ option.optionMessage }}
                      </template>
                      · {{ option.quantity }}개
                      <template v-if="option.price > 0">
                        (+{{ won(option.price) }})
                      </template>
                    </li>
                  </ul>
                  <p v-else class="cart_item_options">
                    수량 {{ product.productStock }}개
                  </p>
                  <button
                    type="button"
                    class="cart_item_edit"
                    @click="openOptionModal(product.cartId)"
                  >
                    옵션 수정
                  </button>
                </div>
                <div class="cart_item_price">
                  <p v-if="hasItemDiscount(product)">
                    <strong>{{ itemDiscountRate(product) }}%</strong>
                    <s>{{ won(product.regularPrice) }}</s>
                  </p>
                  <b>{{
                    product.isFree === true ? "무료" : won(product.purchasePrice)
                  }}</b>
                </div>
                <button
                  type="button"
                  class="cart_item_remove"
                  :aria-label="product.title + ' 삭제'"
                  @click="removeCartItems([product.cartId])"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </li>
            </ul>

            <div class="cart_group_foot">
              <p>
                배송비
                <b>{{ won(warehouseDeliveryFee(wareKey)) }}</b>
                <span>선택한 상품 기준 · {{ warehouse.deliveryCompany || "택배" }}</span>
              </p>
              <input
                v-model="warehouse.requestMessage"
                type="text"
                class="cart_input"
                maxlength="50"
                placeholder="배송 메시지 (선택)"
                :aria-label="(warehouse.name ?? '출고지') + ' 배송 메시지'"
              />
            </div>
          </div>

          <!-- 판매중지 콘텐츠 -->
          <div
            v-if="contentUnavailableCartList.length > 0"
            class="cart_group cart_group--disabled"
          >
            <div class="cart_group_head">
              <span class="cart_group_title">판매중지된 콘텐츠</span>
              <span class="cart_group_meta">주문에서 제외돼요</span>
            </div>
            <ul>
              <li
                v-for="item in contentUnavailableCartList"
                :key="item.cartId"
                class="cart_item"
              >
                <span class="cart_item_check"></span>
                <div class="cart_item_thumb">
                  <img
                    v-if="item.thumbnailLink"
                    :src="IMAGE_BASE + item.thumbnailLink"
                    :alt="item.title"
                  />
                  <img v-else src="@/assets/img/null_img.jpg" :alt="item.title" />
                </div>
                <div class="cart_item_info">
                  <span class="cart_item_badge cart_item_badge--stop"
                    >판매중지</span
                  >
                  <p class="cart_item_title">{{ item.title }}</p>
                </div>
                <div class="cart_item_price">
                  <b>{{
                    item.isFree === true
                      ? "무료"
                      : won(item.sellingPrice * item.quantity)
                  }}</b>
                </div>
                <button
                  type="button"
                  class="cart_item_remove"
                  :aria-label="item.title + ' 삭제'"
                  @click="removeCartItems([item.cartId])"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </li>
            </ul>
          </div>
        </section>

        <!-- 주문자 정보 -->
        <section class="cart_card cart_buyer" aria-labelledby="cart-buyer-title">
          <h2 id="cart-buyer-title">주문자 정보</h2>
          <div class="cart_field">
            <label for="cart-buyer-name">이름 <em>*</em></label>
            <input
              id="cart-buyer-name"
              v-model.trim="orderInfo.orderName"
              type="text"
              class="cart_input"
              autocomplete="name"
              placeholder="실명 입력"
            />
          </div>
          <div class="cart_field">
            <label for="cart-buyer-phone">연락처 <em>*</em></label>
            <input
              id="cart-buyer-phone"
              v-model.trim="orderInfo.orderPhone"
              type="tel"
              inputmode="numeric"
              class="cart_input"
              autocomplete="tel"
              placeholder="010-0000-0000"
            />
          </div>
          <div class="cart_field">
            <label for="cart-buyer-email">이메일 <em>*</em></label>
            <input
              id="cart-buyer-email"
              v-model.trim="orderInfo.orderEmail"
              type="email"
              class="cart_input"
              autocomplete="email"
              placeholder="example@email.com"
            />
          </div>
          <p class="cart_hint">결제 안내와 주문 내역은 이 정보로 보내 드려요.</p>
        </section>
      </div>

      <!-- 결제 영역: 데스크톱에서는 오른쪽에 고정 -->
      <aside class="cart_side">
        <section aria-labelledby="cart-summary-title">
          <h2 id="cart-summary-title" class="cart_side_title">결제 금액</h2>
          <div class="cart_card cart_summary" :class="{ loading: isSummaryLoading }">
            <dl>
              <div>
                <dt>선택 항목 {{ selectedCount }}개</dt>
                <dd>{{ won(summary.regular) }}</dd>
              </div>
              <div v-if="summary.discount > 0" class="cart_summary_discount">
                <dt>
                  <button
                    type="button"
                    :aria-expanded="isDiscountOpen"
                    @click="isDiscountOpen = !isDiscountOpen"
                  >
                    할인 금액
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      :class="{ open: isDiscountOpen }"
                      aria-hidden="true"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                </dt>
                <dd>-{{ won(summary.discount) }}</dd>
              </div>
              <div
                v-if="summary.discount > 0 && isDiscountOpen"
                class="cart_summary_sub"
              >
                <dt>즉시 할인</dt>
                <dd>-{{ won(summary.discount) }}</dd>
              </div>
              <div v-if="selectedProductCount > 0">
                <dt>배송비</dt>
                <dd>{{ summary.delivery > 0 ? won(summary.delivery) : "무료" }}</dd>
              </div>
            </dl>
            <div class="cart_summary_total">
              <span>총 결제 금액</span>
              <b>{{ won(summary.paid) }}</b>
            </div>
          </div>
        </section>

        <section v-if="summary.paid > 0" aria-labelledby="cart-method-title">
          <h2 id="cart-method-title" class="cart_side_title">결제수단</h2>
          <div class="cart_card cart_methods" role="radiogroup">
            <label
              v-for="method in payMethods"
              :key="method.value"
              class="cart_method"
              :class="{ checked: gopaymethod === method.value }"
            >
              <input
                v-model="gopaymethod"
                type="radio"
                name="cart-pay-method"
                :value="method.value"
              />
              <span class="cart_method_name">{{ method.label }}</span>
              <span class="cart_method_desc">{{ method.desc }}</span>
            </label>
          </div>
        </section>

        <div class="cart_side_pay">
          <button
            type="button"
            class="cart_pay_btn"
            :disabled="selectedCount === 0 || isPaying"
            @click="submitPayment"
          >
            {{ payButtonLabel }}
          </button>
          <p class="cart_agree">
            주문 내용을 확인했으며,
            <router-link to="/policy/guide">이용약관</router-link> 및
            <router-link to="/policy/info">개인정보취급방침</router-link>과 결제에
            동의합니다.
          </p>
        </div>
      </aside>
    </div>

    <!-- 모바일 하단 결제바 -->
    <div v-if="isLoaded && !isEmpty" class="cart_mobile_bar">
      <div class="cart_mobile_total">
        <span>총 결제 금액</span>
        <b>{{ won(summary.paid) }}</b>
      </div>
      <button
        type="button"
        class="cart_pay_btn"
        :disabled="selectedCount === 0 || isPaying"
        @click="submitPayment"
      >
        {{ payButtonLabel }}
      </button>
    </div>

    <!-- 옵션 모달 창 -->
    <productOptionModal
      v-if="modalVisible"
      @close="modalVisible = false"
      @updateComplete="updateCompleteCart"
      :cartIdx="modalData"
    ></productOptionModal>

    <!-- 결제 요청 폼 (이니시스) - pc -->
    <form
      ref="pcPayFormRef"
      method="post"
      action="https://nodeblack.net/api/pay/mall/v2/pc"
      target="PayForm"
      name="pcPayForm"
      class="hidden"
    >
      <input type="hidden" name="buyername" :value="orderInfo.orderName" />
      <input type="hidden" name="buyertel" :value="orderInfo.orderPhone" />
      <input type="hidden" name="buyeremail" :value="orderInfo.orderEmail" />
      <input type="hidden" name="buyerAddress" :value="orderInfo.address" />
      <input
        type="hidden"
        name="buyerDetailedAddress"
        :value="orderInfo.detailAddress"
      />
      <input type="hidden" name="buyerZipCode" :value="orderInfo.postcode" />
      <input type="hidden" name="cartIdSet" :value="cartItemList" />
      <input type="hidden" name="gopaymethod" :value="gopaymethod" />
      <input type="hidden" name="token" :value="token" />
      <input
        type="hidden"
        name="deliveryRequestMessageJson"
        :value="deliveryRequestMessageJson"
      />
    </form>

    <!-- 결제 요청 폼 (이니시스) - mobile -->
    <form
      ref="mPayFormRef"
      method="post"
      action="https://nodeblack.net/api/pay/mall/v2/mobile"
      name="mPayForm"
      class="hidden"
    >
      <input type="hidden" name="P_UNAME" :value="orderInfo.orderName" />
      <input type="hidden" name="P_MOBILE" :value="orderInfo.orderPhone" />
      <input type="hidden" name="P_EMAIL" :value="orderInfo.orderEmail" />
      <input type="hidden" name="buyerAddress" :value="orderInfo.address" />
      <input
        type="hidden"
        name="buyerDetailedAddress"
        :value="orderInfo.detailAddress"
      />
      <input type="hidden" name="buyerZipCode" :value="orderInfo.postcode" />
      <input type="hidden" name="cartIdSet" :value="cartItemList" />
      <input type="hidden" name="P_INI_PAYMENT" :value="mobileGopaymethod" />
      <input type="hidden" name="token" :value="token" />
      <input
        type="hidden"
        name="deliveryRequestMessageJson"
        :value="deliveryRequestMessageJson"
      />
    </form>

    <iframe
      ref="payFrameRef"
      id="PayForm"
      name="PayForm"
      title="결제"
      src="about:blank"
      class="cart_pay_frame"
    />
  </div>
</template>

<script setup>
import axios from "axios";
import router from "@/router";
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

import productOptionModal from "@/components/productOptionModify.vue"; // 옵션 수정 모달

const IMAGE_BASE = "https://eduwind-api.nodeblack.net/";

const isLoaded = ref(false);
const productCartInfo = ref({ warehouses: [] }); // 상품 장바구니 (출고지별)
const contentCartInfo = ref({ contents: [] }); // 콘텐츠 장바구니

// 판매중지 상품 필터
const contentAvailableCartList = ref([]);
const contentUnavailableCartList = ref([]);

// 주문자 정보 (배송지는 주문서 API 기본값을 그대로 사용)
const orderInfo = ref({
  orderName: "",
  orderPhone: "",
  orderEmail: "",
  postcode: "",
  address: "",
  detailAddress: "",
});
const isBuyerPrefilled = ref(false);

// 결제수단 (모바일 결제는 이니시스 P_INI_PAYMENT 값 사용)
const payMethods = [
  {
    value: "Card",
    mobile: "CARD",
    label: "신용카드",
    desc: "국내 신용·체크카드",
  },
  {
    value: "DirectBank",
    mobile: "BANK",
    label: "실시간 계좌이체",
    desc: "내 계좌에서 바로 이체",
  },
  {
    value: "VBank",
    mobile: "VBANK",
    label: "가상계좌",
    desc: "안내된 계좌로 입금하면 주문 완료",
  },
];
const gopaymethod = ref("Card");
const mobileGopaymethod = computed(
  () =>
    payMethods.find((method) => method.value === gopaymethod.value)?.mobile ||
    "CARD",
);

const token = ref("");
const cartItemList = ref(""); // 결제 요청용 장바구니 아이디 목록 (콤마 구분)
const deliveryRequestMessageJson = ref("[]"); // 출고지별 배송 메시지
const isPaying = ref(false);
const isDiscountOpen = ref(false);

const pcPayFormRef = ref(null);
const mPayFormRef = ref(null);
const payFrameRef = ref(null);

const won = (value) => `${Number(value || 0).toLocaleString("ko-kr")}원`;

const allProducts = computed(() =>
  (productCartInfo.value.warehouses || []).flatMap(
    (warehouse) => warehouse.products,
  ),
);
const selectableItems = computed(() => [
  ...contentAvailableCartList.value,
  ...allProducts.value,
]);
const selectedItems = computed(() =>
  selectableItems.value.filter((item) => item.isSelected),
);
const selectedCount = computed(() => selectedItems.value.length);
const selectedProductCount = computed(
  () => allProducts.value.filter((product) => product.isSelected).length,
);
const isAllSelected = computed(
  () =>
    selectableItems.value.length > 0 &&
    selectableItems.value.every((item) => item.isSelected),
);
const isEmpty = computed(
  () =>
    isLoaded.value &&
    allProducts.value.length === 0 &&
    (contentCartInfo.value.contents || []).length === 0,
);

const toggleItems = (items, $event) => {
  items.forEach((item) => {
    item.isSelected = $event.target.checked;
  });
};

// 정가 정보가 있고 실제로 할인된 경우에만 할인 표시
const hasItemDiscount = (item) =>
  item.isFree !== true && item.regularPrice > item.purchasePrice;
const itemDiscountRate = (item) =>
  item.discountRate > 0
    ? item.discountRate
    : Math.round((1 - item.purchasePrice / item.regularPrice) * 100);

// 출고지 별 배송비 (선택한 상품 기준)
const warehouseDeliveryFee = (wareKey) => {
  const warehouse = productCartInfo.value.warehouses[wareKey];
  const productPrice = warehouse.products
    .filter((product) => product.isSelected)
    .reduce((total, product) => total + product.purchasePrice, 0);
  if (productPrice <= 0 || productPrice > warehouse.deliveryFreeOption) {
    return 0;
  }
  return warehouse.deliveryFee;
};

// 결제 금액: 선택 항목 기준으로 주문서 API에서 계산한 금액 (실패 시 화면에서 계산)
const summary = ref({ regular: 0, discount: 0, delivery: 0, paid: 0 });
const isSummaryLoading = ref(false);

const calcSummaryFallback = () => {
  const purchase = selectedItems.value.reduce(
    (total, item) => total + item.purchasePrice,
    0,
  );
  const delivery = (productCartInfo.value.warehouses || []).reduce(
    (total, warehouse, wareKey) => total + warehouseDeliveryFee(wareKey),
    0,
  );
  return { regular: purchase, discount: 0, delivery, paid: purchase + delivery };
};

const payButtonLabel = computed(() => {
  if (selectedCount.value === 0) return "주문할 항목을 선택해 주세요";
  if (summary.value.paid > 0) return `${won(summary.value.paid)} 결제하기`;
  return "무료로 주문하기";
});

// 장바구니 GET
const getCart = async () => {
  try {
    const url = "/new_api/customer/cart/v2/list";
    const { data, status } = await axios.get(url);
    if (status !== 200 || data.code !== "200") {
      alert(
        "장바구니를 불러오는데 실패했습니다.\n\n새로고침 후 다시 시도해 주세요.",
      );
      return;
    }

    // 다시 불러와도 화면에서 바꾼 선택 상태와 입력한 배송 메시지는 유지
    const prevSelected = Object.fromEntries(
      selectableItems.value.map((item) => [item.cartId, item.isSelected]),
    );
    const prevMessages = Object.fromEntries(
      (productCartInfo.value.warehouses || []).map((warehouse) => [
        warehouse.warehouseId,
        warehouse.requestMessage,
      ]),
    );

    ({ productInfo: productCartInfo.value, contentInfo: contentCartInfo.value } =
      data.data); // 상품, 콘텐츠 장바구니

    productCartInfo.value.warehouses.forEach((warehouse) => {
      warehouse.requestMessage = prevMessages[warehouse.warehouseId] ?? null;
    });
    [
      ...contentCartInfo.value.contents,
      ...productCartInfo.value.warehouses.flatMap(
        (warehouse) => warehouse.products,
      ),
    ].forEach((item) => {
      if (item.cartId in prevSelected) item.isSelected = prevSelected[item.cartId];
    });

    contentAvailableCartList.value = contentCartInfo.value.contents.filter(
      (item) => item.itemStatus !== "STOP",
    );
    contentUnavailableCartList.value = contentCartInfo.value.contents.filter(
      (item) => item.itemStatus === "STOP",
    );
  } catch (err) {
    console.error(err);
  } finally {
    isLoaded.value = true;
  }
};

// 주문서 정보 (주문자 정보, 기본 배송지, 결제 금액)
const getOrderForm = async (cartIdSet) => {
  const url = "/new_api/customer/order/v2/form";
  const { data, status } = await axios.post(url, { cartIdSet });
  if (status !== 200 || data.code !== "200") {
    throw new Error("주문서 정보를 불러오지 못했습니다.");
  }
  return data.data;
};

const applyOrderForm = (form) => {
  if (!isBuyerPrefilled.value) {
    orderInfo.value.orderName = orderInfo.value.orderName || form.orderName || "";
    orderInfo.value.orderPhone =
      orderInfo.value.orderPhone || form.orderPhone || "";
    orderInfo.value.orderEmail =
      orderInfo.value.orderEmail || form.orderEmail || "";
    isBuyerPrefilled.value = true;
  }
  // 주문서 페이지(myOrderPage3)와 같은 기본 배송지 사용
  orderInfo.value.postcode = form.orderZipCode || "06177";
  orderInfo.value.address = form.orderAddress || "서울 강남구 영동대로 330";
  orderInfo.value.detailAddress = form.orderDetailedAddress || "총회교육개발원";
};

let summaryTimer = null;
let summaryRequestId = 0;
const refreshSummary = () => {
  clearTimeout(summaryTimer);
  const cartIdSet = selectedItems.value.map((item) => item.cartId);
  if (cartIdSet.length === 0) {
    summary.value = { regular: 0, discount: 0, delivery: 0, paid: 0 };
    return;
  }
  summary.value = calcSummaryFallback();
  isSummaryLoading.value = true;
  const requestId = ++summaryRequestId;
  summaryTimer = setTimeout(async () => {
    try {
      const form = await getOrderForm(cartIdSet);
      if (requestId !== summaryRequestId) return;
      summary.value = {
        regular: form.totalOrderRegularPrice,
        discount: form.totalOrderDiscountAmount,
        delivery: form.totalOrderDeliveryFee,
        paid: form.totalPaidAmount,
      };
      applyOrderForm(form);
    } catch (err) {
      console.error(err);
    } finally {
      if (requestId === summaryRequestId) isSummaryLoading.value = false;
    }
  }, 250);
};

watch(
  () => selectedItems.value.map((item) => item.cartId).join(","),
  refreshSummary,
);

// 장바구니 삭제
const removeCartItems = async (cartIds) => {
  if (cartIds.length === 0) {
    alert("삭제할 항목을 선택해 주세요.");
    return;
  }
  if (!confirm(`${cartIds.length}개 항목을 장바구니에서 삭제할까요?`)) return;
  try {
    const url = "/new_api/customer/cart/v2/delete-cart-all";
    const { data, status } = await axios.delete(url, { data: cartIds });
    if (status === 200 && data.code === "200") {
      await getCart();
      refreshSummary();
    }
  } catch (err) {
    console.error(err);
  }
};

const deleteSelected = () =>
  removeCartItems(selectedItems.value.map((item) => item.cartId));

const modalData = ref(null);
const modalVisible = ref(false);
// 옵션 변경 열기
const openOptionModal = (cartId) => {
  modalData.value = cartId;
  modalVisible.value = true;
};
// 장바구니 변경 성공시
const updateCompleteCart = async () => {
  await getCart();
  refreshSummary();
  modalVisible.value = false;
};

const setDeliveryRequestMessageJson = () => {
  deliveryRequestMessageJson.value = JSON.stringify(
    productCartInfo.value.warehouses.map((warehouse) => ({
      warehouseId: warehouse.warehouseId,
      requestMessage: warehouse.requestMessage,
    })),
  );
};

const validateBuyer = () => {
  const { orderName, orderPhone, orderEmail } = orderInfo.value;
  if (!orderName) return "주문자 이름을 입력해 주세요.";
  if (!/^[0-9-]{9,13}$/.test(orderPhone || "")) {
    return "주문자 연락처를 정확히 입력해 주세요.";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orderEmail || "")) {
    return "주문자 이메일을 정확히 입력해 주세요.";
  }
  return "";
};

// 결제 요청 (pc: iframe 안에서 이니시스 결제창, mobile: 결제 페이지로 이동)
const submitPayment = async () => {
  const cartIdSet = selectedItems.value.map((item) => item.cartId);
  if (cartIdSet.length === 0) {
    alert("주문할 상품이나 콘텐츠를 선택해 주세요.");
    return;
  }
  const buyerError = validateBuyer();
  if (buyerError) {
    alert(buyerError);
    document.getElementById("cart-buyer-name")?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    return;
  }

  const localStorageToken = localStorage.getItem("authorization");
  if (!localStorageToken) {
    console.error("토큰 값이 일치하지 않습니다.");
    alert("잘못된 요청입니다.");
    window.location.reload();
    return;
  }

  isPaying.value = true;
  try {
    // 결제 직전에 선택 항목으로 주문서를 다시 확인
    applyOrderForm(await getOrderForm(cartIdSet));
    token.value = localStorageToken;
    cartItemList.value = cartIdSet.join(",");
    setDeliveryRequestMessageJson();
    await nextTick();

    payFrameRef.value.style.display = "block";
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (window.matchMedia("(max-width: 767px)").matches) {
      mPayFormRef.value.submit();
    } else {
      pcPayFormRef.value.submit();
    }
  } catch (err) {
    console.error(err);
    alert("결제를 시작하지 못했습니다.\n\n잠시 후 다시 시도해 주세요.");
    payFrameRef.value.style.display = "none";
    isPaying.value = false;
  }
};

// 결제창(iframe)에서 보내는 메시지 처리
const handlePayMessage = (event) => {
  if (typeof event.data !== "string") return;
  let eventData;
  try {
    eventData = JSON.parse(event.data);
  } catch (err) {
    return;
  }
  if (eventData.mod === "close") {
    // 이니시스 닫힘
    alert("사용자 요청에 의해 결제가 취소 되었습니다.");
    router.go(0);
  } else if (eventData.mod === "free" && eventData.moid) {
    // 무료결제
    router.push(`/mypage/order_done/${eventData.moid}`);
  } else if (eventData.mod === "exception") {
    // 실패
    alert(eventData.returnMsg);
    document.location.reload();
  }
};

onMounted(async () => {
  window.addEventListener("message", handlePayMessage);
  await getCart();
  refreshSummary();

  // 선택한 항목이 없으면 전체 항목으로 주문자 정보만 미리 채움
  if (selectedCount.value === 0 && selectableItems.value.length > 0) {
    try {
      applyOrderForm(
        await getOrderForm(selectableItems.value.map((item) => item.cartId)),
      );
    } catch (err) {
      console.error(err);
    }
  }
});

onUnmounted(() => {
  window.removeEventListener("message", handlePayMessage);
  clearTimeout(summaryTimer);
});
</script>

<style scoped>
.cart_page {
  --cart-navy: var(--main-navy, #0c3388);
  --cart-navy-dark: #0a2a70;
  --cart-ink: #1b1d21;
  --cart-body: #3a3d44;
  --cart-muted: #868e96;
  --cart-line: #e9ecef;
  --cart-soft: #f8f9fa;
  --cart-sale: #fa5252;
  --cart-sticky-top: var(--sticky-top, var(--nav-height, 0px));

  max-width: 72rem;
  margin-left: auto;
  margin-right: auto;
  padding: 2rem 1.5rem 6rem;
  color: var(--cart-ink);
}

.cart_head {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.cart_head h1 {
  font-size: 1.625rem;
  font-weight: 700;
}

.cart_head p {
  color: var(--cart-muted);
  font-size: 0.9375rem;
}

.cart_head p b {
  color: var(--cart-ink);
}

/* 빈 장바구니 */
.cart_empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 6rem 0;
  color: var(--cart-muted);
}

.cart_empty p {
  color: var(--cart-body);
  font-size: 1.125rem;
}

/* 레이아웃 */
.cart_layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 25rem;
  align-items: start;
  gap: 2rem;
}

.cart_main {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.cart_card {
  border: 1px solid var(--cart-line);
  border-radius: 0.5rem;
  background: #fff;
}

/* 공통 버튼·입력 */
.cart_ghost_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.875rem;
  padding: 0 0.875rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background: #fff;
  color: var(--cart-ink);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.2s;
}

.cart_ghost_btn:hover {
  background: var(--cart-soft);
}

.cart_input {
  width: 100%;
  height: 2.75rem;
  padding: 0 0.875rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #fff;
  font-size: 0.9375rem;
  outline: none;
  transition: border-color 0.2s;
}

.cart_input:focus {
  border-color: var(--cart-navy);
}

/* 동그란 체크박스 */
.cart_check {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  font-size: 0.9375rem;
}

.cart_check input {
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  border: 1.5px solid #ced4da;
  border-radius: 9999px;
  background: #fff;
  appearance: none;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.cart_check input:checked {
  border-color: var(--cart-navy);
  background: var(--cart-navy)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12.5l4.5 4.5L19 7.5'/%3E%3C/svg%3E")
    center / 0.75rem no-repeat;
}

.cart_check input:focus-visible {
  outline: 2px solid var(--cart-navy);
  outline-offset: 2px;
}

.cart_check input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.cart_check b {
  color: var(--cart-navy);
}

/* 장바구니 목록 */
.cart_list_bar {
  position: sticky;
  top: var(--cart-sticky-top);
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--cart-line);
  border-radius: 0.5rem 0.5rem 0 0;
  background: #fff;
  transition: top 0.7s ease-in-out;
}

.cart_group + .cart_group {
  border-top: 8px solid var(--cart-soft);
}

.cart_group_head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.25rem;
}

.cart_group_head .cart_check span,
.cart_group_title {
  font-weight: 700;
}

.cart_group_meta {
  color: var(--cart-muted);
  font-size: 0.8125rem;
}

.cart_item {
  position: relative;
  display: grid;
  grid-template-columns: 1.25rem 7.375rem minmax(0, 1fr) auto;
  align-items: start;
  gap: 1rem;
  padding: 1rem 3rem 1rem 1rem;
}

.cart_item + .cart_item {
  border-top: 1px solid var(--cart-line);
}

.cart_item_check {
  padding-top: 0.25rem;
}

.cart_item_thumb {
  display: block;
  aspect-ratio: 3 / 2;
  border: 1px solid var(--cart-line);
  border-radius: 0.5rem;
  overflow: hidden;
}

.cart_item_thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cart_item_info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
}

.cart_item_badge {
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  background: #eef2fb;
  color: var(--cart-navy);
  font-size: 0.6875rem;
  font-weight: 700;
}

.cart_item_badge--stop {
  background: #fff0f0;
  color: var(--cart-sale);
}

.cart_item_title {
  display: -webkit-box;
  overflow: hidden;
  color: var(--cart-ink);
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: keep-all;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

a.cart_item_title:hover {
  text-decoration: underline;
}

.cart_item_options {
  color: var(--cart-muted);
  font-size: 0.8125rem;
  line-height: 1.5;
}

.cart_item_edit {
  margin-top: 0.125rem;
  color: var(--cart-navy);
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cart_item_price {
  text-align: right;
  white-space: nowrap;
}

.cart_item_price p {
  font-size: 0.875rem;
}

.cart_item_price strong {
  margin-right: 0.25rem;
  color: var(--cart-sale);
  font-weight: 500;
}

.cart_item_price s {
  color: var(--cart-muted);
}

.cart_item_price b {
  font-size: 1rem;
  font-weight: 700;
}

.cart_item_remove {
  position: absolute;
  top: 0.875rem;
  right: 0.875rem;
  display: grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  color: var(--cart-muted);
  transition: background-color 0.2s, color 0.2s;
}

.cart_item_remove:hover {
  background: var(--cart-soft);
  color: var(--cart-ink);
}

.cart_group_foot {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin: 0 1rem 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background: var(--cart-soft);
  font-size: 0.875rem;
}

.cart_group_foot p {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.cart_group_foot p span {
  color: var(--cart-muted);
  font-size: 0.8125rem;
}

.cart_group--disabled .cart_item_thumb,
.cart_group--disabled .cart_item_title,
.cart_group--disabled .cart_item_price {
  opacity: 0.5;
}

/* 주문자 정보 */
.cart_buyer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.25rem 1.5rem;
}

.cart_buyer h2 {
  font-size: 1rem;
  font-weight: 700;
}

.cart_field label {
  display: block;
  margin-bottom: 0.375rem;
  color: var(--cart-body);
  font-size: 0.875rem;
  font-weight: 600;
}

.cart_field em {
  color: var(--cart-sale);
  font-style: normal;
}

.cart_hint {
  color: var(--cart-muted);
  font-size: 0.8125rem;
}

/* 결제 영역 */
.cart_side {
  position: sticky;
  top: calc(var(--cart-sticky-top) + 1.5rem);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-height: calc(100vh - var(--cart-sticky-top) - 3rem);
  overflow-y: auto;
  transition: top 0.7s ease-in-out;
}

.cart_side_title {
  margin-bottom: 0.625rem;
  font-size: 1.125rem;
  font-weight: 700;
}

.cart_summary {
  padding: 1rem 1.25rem;
  transition: opacity 0.2s;
}

.cart_summary.loading {
  opacity: 0.6;
}

.cart_summary dl {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid var(--cart-line);
}

.cart_summary dl > div {
  display: flex;
  justify-content: space-between;
  color: var(--cart-body);
  font-size: 0.875rem;
  font-weight: 500;
}

.cart_summary_discount,
.cart_summary_discount button {
  color: var(--cart-sale) !important;
}

.cart_summary_discount button {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  font-weight: 500;
}

.cart_summary_discount svg {
  transition: transform 0.2s;
}

.cart_summary_discount svg.open {
  transform: rotate(180deg);
}

.cart_summary_sub {
  margin-top: -0.25rem;
  padding-left: 0.75rem;
  border-left: 2px solid var(--cart-line);
  color: var(--cart-muted) !important;
  font-size: 0.8125rem !important;
}

.cart_summary_total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-top: 0.875rem;
  font-weight: 600;
}

.cart_summary_total b {
  font-size: 1.375rem;
  font-weight: 800;
}

.cart_methods {
  overflow: hidden;
}

.cart_method {
  display: grid;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  gap: 0.125rem 0.75rem;
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cart_method + .cart_method {
  border-top: 1px solid var(--cart-line);
}

.cart_method:hover {
  background: var(--cart-soft);
}

.cart_method input {
  grid-row: 1 / span 2;
  width: 1.25rem;
  height: 1.25rem;
  margin-top: 0.0625rem;
  border: 1.5px solid #ced4da;
  border-radius: 9999px;
  appearance: none;
  cursor: pointer;
}

.cart_method input:checked {
  border: 6px solid var(--cart-navy);
}

.cart_method input:focus-visible {
  outline: 2px solid var(--cart-navy);
  outline-offset: 2px;
}

.cart_method_name {
  font-size: 0.9375rem;
  font-weight: 600;
}

.cart_method_desc {
  grid-column: 2;
  color: var(--cart-muted);
  font-size: 0.8125rem;
}

.cart_method.checked .cart_method_name {
  color: var(--cart-navy);
}

.cart_pay_btn {
  width: 100%;
  height: 3.25rem;
  border-radius: 0.5rem;
  background: var(--cart-navy);
  color: #fff;
  font-size: 1.125rem;
  font-weight: 600;
  transition: background-color 0.2s;
}

.cart_pay_btn:hover {
  background: var(--cart-navy-dark);
}

.cart_pay_btn:disabled {
  background: #c8cbd0;
  cursor: not-allowed;
}

.cart_agree {
  margin-top: 0.625rem;
  color: var(--cart-muted);
  font-size: 0.75rem;
  line-height: 1.6;
  text-align: center;
  word-break: keep-all;
}

.cart_agree a {
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* 모바일 하단 결제바 (데스크톱은 결제 영역의 버튼 사용) */
.cart_mobile_bar {
  display: none;
}

.cart_pay_frame {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  border: 0;
}

/* 태블릿 이하: 1단 + 하단 결제바 */
@media (max-width: 1023px) {
  .cart_page {
    padding: 1.5rem 1rem 11rem;
  }

  .cart_layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .cart_side {
    position: static;
    max-height: none;
    overflow: visible;
  }

  .cart_side_pay {
    display: none;
  }

  .cart_mobile_bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 15;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
    border-top: 1px solid var(--cart-line);
    background: #fff;
    box-shadow: 0 -0.375rem 1.25rem rgba(16, 24, 40, 0.08);
  }

  .cart_mobile_total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .cart_mobile_total b {
    font-size: 1.125rem;
    font-weight: 800;
  }
}

/* 모바일: 상품 행을 2줄로 (썸네일·정보 / 가격) */
@media (max-width: 767px) {
  .cart_head h1 {
    font-size: 1.375rem;
  }

  .cart_item {
    grid-template-columns: 1.25rem 5.5rem minmax(0, 1fr);
    gap: 0.75rem;
    padding-right: 2.75rem;
  }

  .cart_item_price {
    grid-column: 3;
  }

  .cart_item_title {
    font-size: 0.9375rem;
  }
}
</style>
